import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TenantAdminSectionHeader from "./TenantAdminSectionHeader";
import { useUser } from "../../context/UserContext";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../../constants/tenantGovernancePermissions";
import { getRoleNames, hasAnyPermission } from "../../utils/tenantAdminAccess";
import {
  getBrandingDocumentsSettings,
  updateBrandingDocumentsSettings,
} from "../../services/tenantSettingsService";

const LAYOUT_PRESETS = ["standaard", "compact", "uitgebreid"];

const DEFAULT_SETTINGS = {
  branding: {
    primaryColor: "#0f172a",
    secondaryColor: "#2563eb",
    logoUrl: "",
    fontFamily: "Inter",
    footerText: "",
    documentHeaderText: "",
    documentFooterText: "",
  },
  documentTemplates: {
    workorder: {
      enabled: true,
      showHours: true,
      showMaterials: true,
      showPrices: false,
      showPhotos: true,
      showCustomerSignature: true,
      showTechnicianSignature: true,
      showCompanyDetails: true,
      showQrCode: false,
      layoutPreset: "standaard",
    },
    mandayStatement: {
      enabled: true,
      showPerTechnician: true,
      showTotals: true,
      showApprovalBlock: true,
      layoutPreset: "standaard",
    },
    inspectionReport: {
      enabled: true,
      showFindings: true,
      showPhotos: true,
      showRiskClassification: true,
      showAdvice: true,
      layoutPreset: "standaard",
    },
  },
};

function mergeSettings(source = {}) {
  return {
    branding: {
      ...DEFAULT_SETTINGS.branding,
      ...(source.branding || {}),
    },
    documentTemplates: {
      workorder: {
        ...DEFAULT_SETTINGS.documentTemplates.workorder,
        ...(source.documentTemplates?.workorder || {}),
      },
      mandayStatement: {
        ...DEFAULT_SETTINGS.documentTemplates.mandayStatement,
        ...(source.documentTemplates?.mandayStatement || {}),
      },
      inspectionReport: {
        ...DEFAULT_SETTINGS.documentTemplates.inspectionReport,
        ...(source.documentTemplates?.inspectionReport || {}),
      },
    },
  };
}

function hasFullTenantAdminAccess(user) {
  const roleNames = new Set(getRoleNames(user));
  return roleNames.has("tenantadmin") || roleNames.has("platformadmin");
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, disabled, type = "text", placeholder = "" }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-500"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      <textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        rows={3}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-500"
      />
    </label>
  );
}

function CheckboxField({ label, checked, onChange, disabled }) {
  return (
    <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked === true}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
        className="h-4 w-4 rounded border-slate-300"
      />
      <span>{label}</span>
    </label>
  );
}

function LayoutPresetField({ value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">Layout preset</span>
      <select
        value={value || "standaard"}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-500"
      >
        {LAYOUT_PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {preset}
          </option>
        ))}
      </select>
    </label>
  );
}

function PreviewCard({ settings }) {
  const branding = settings.branding;
  const workorder = settings.documentTemplates.workorder;
  const lines = [
    workorder.showHours ? "Uren: 2,50 uur normaal tarief" : null,
    workorder.showMaterials ? "Materiaal: rookmelder, kabel en montagemateriaal" : null,
    workorder.showPrices ? "Bedrag: EUR 248,50 excl. btw" : null,
    workorder.showPhotos ? "Foto's: 3 bijlagen opgenomen" : null,
    workorder.showCustomerSignature ? "Klanthandtekening: zichtbaar" : null,
    workorder.showTechnicianSignature ? "Monteurhandtekening: zichtbaar" : null,
    workorder.showQrCode ? "QR-code: zichtbaar" : null,
  ].filter(Boolean);

  return (
    <SectionCard
      title="Preview"
      description="Visuele voorbeeldkaart. Deze preview wijzigt nog geen bestaande PDF-export."
    >
      <div
        className="overflow-hidden rounded-xl border border-slate-200 bg-white"
        style={{ fontFamily: branding.fontFamily || "Inter" }}
      >
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 text-white"
          style={{ backgroundColor: branding.primaryColor || "#0f172a" }}
        >
          <div>
            <p className="text-xs uppercase tracking-[0.18em] opacity-80">Logboek24</p>
            <h3 className="text-lg font-semibold">Werkbon voorbeeld</h3>
          </div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt="Logo"
              className="h-12 w-24 rounded bg-white object-contain p-1"
            />
          ) : (
            <div className="flex h-12 w-24 items-center justify-center rounded bg-white/15 text-xs">
              Logo
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="mb-3 border-l-4 pl-3" style={{ borderColor: branding.secondaryColor }}>
            <p className="text-sm font-medium text-slate-900">Bedrijfsnaam placeholder</p>
            <p className="text-xs text-slate-500">
              {branding.documentHeaderText || "Koptekst documenten"}
            </p>
          </div>
          <div className="space-y-2">
            {lines.length ? (
              lines.map((line) => (
                <div key={line} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  {line}
                </div>
              ))
            ) : (
              <div className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500">
                Geen werkbonregels geselecteerd voor preview.
              </div>
            )}
          </div>
          <p className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-500">
            {branding.documentFooterText || branding.footerText || "Voettekst documenten"}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

export default function TenantAdminBrandingDocumentsPage() {
  const { user } = useUser();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const canUpdate = useMemo(
    () =>
      hasFullTenantAdminAccess(user) ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsUpdate),
    [user]
  );

  const canViewDocumentLibrary = useMemo(
    () =>
      hasFullTenantAdminAccess(user) ||
      hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsView),
    [user]
  );

  const readOnly = !canUpdate;

  useEffect(() => {
    let alive = true;

    async function loadSettings() {
      setLoading(true);
      setError("");
      setMessage("");
      try {
        const data = await getBrandingDocumentsSettings();
        if (!alive) return;
        setSettings(mergeSettings(data));
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || "Huisstijl en documenten konden niet worden geladen.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      alive = false;
    };
  }, []);

  function updateBranding(field, value) {
    setSettings((prev) => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value,
      },
    }));
  }

  function updateTemplate(templateKey, field, value) {
    setSettings((prev) => ({
      ...prev,
      documentTemplates: {
        ...prev.documentTemplates,
        [templateKey]: {
          ...prev.documentTemplates[templateKey],
          [field]: value,
        },
      },
    }));
  }

  async function saveSettings() {
    if (readOnly) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await updateBrandingDocumentsSettings(settings);
      setSettings(mergeSettings(data));
      setMessage(data?.message || "Huisstijl en documentinstellingen opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  }

  const workorder = settings.documentTemplates.workorder;
  const mandayStatement = settings.documentTemplates.mandayStatement;
  const inspectionReport = settings.documentTemplates.inspectionReport;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl">
        <TenantAdminSectionHeader
          eyebrow="Bedrijfsinrichting"
          title="Huisstijl & Documenten"
          description="Beheer logo, kleuren, documentindeling en standaardinstellingen voor werkbonnen, mandagstaten en rapportages."
        />

        {loading ? <p className="text-sm text-slate-600">Instellingen laden...</p> : null}
        {!loading && readOnly ? (
          <p className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            Je hebt alleen leesrechten voor deze instellingen. Velden zijn uitgeschakeld.
          </p>
        ) : null}
        {error ? (
          <p className="mb-4 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}
        {message ? (
          <p className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
            {message}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            <SectionCard
              title="Huisstijl"
              description="Basisgegevens voor toekomstige documenttemplates en previews."
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField
                  label="Primaire kleur"
                  type="color"
                  value={settings.branding.primaryColor}
                  onChange={(value) => updateBranding("primaryColor", value)}
                  disabled={readOnly}
                />
                <TextField
                  label="Secundaire kleur"
                  type="color"
                  value={settings.branding.secondaryColor}
                  onChange={(value) => updateBranding("secondaryColor", value)}
                  disabled={readOnly}
                />
                <TextField
                  label="Logo URL"
                  value={settings.branding.logoUrl}
                  onChange={(value) => updateBranding("logoUrl", value)}
                  disabled={readOnly}
                  placeholder="https://..."
                />
                <TextField
                  label="Lettertype"
                  value={settings.branding.fontFamily}
                  onChange={(value) => updateBranding("fontFamily", value)}
                  disabled={readOnly}
                />
                <TextAreaField
                  label="Koptekst documenten"
                  value={settings.branding.documentHeaderText}
                  onChange={(value) => updateBranding("documentHeaderText", value)}
                  disabled={readOnly}
                />
                <TextAreaField
                  label="Voettekst documenten"
                  value={settings.branding.documentFooterText}
                  onChange={(value) => updateBranding("documentFooterText", value)}
                  disabled={readOnly}
                />
              </div>
            </SectionCard>

            <SectionCard title="Werkbon">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <CheckboxField label="Uren tonen" checked={workorder.showHours} onChange={(value) => updateTemplate("workorder", "showHours", value)} disabled={readOnly} />
                <CheckboxField label="Materialen tonen" checked={workorder.showMaterials} onChange={(value) => updateTemplate("workorder", "showMaterials", value)} disabled={readOnly} />
                <CheckboxField label="Prijzen tonen" checked={workorder.showPrices} onChange={(value) => updateTemplate("workorder", "showPrices", value)} disabled={readOnly} />
                <CheckboxField label="Foto's tonen" checked={workorder.showPhotos} onChange={(value) => updateTemplate("workorder", "showPhotos", value)} disabled={readOnly} />
                <CheckboxField label="Klanthandtekening tonen" checked={workorder.showCustomerSignature} onChange={(value) => updateTemplate("workorder", "showCustomerSignature", value)} disabled={readOnly} />
                <CheckboxField label="Monteurhandtekening tonen" checked={workorder.showTechnicianSignature} onChange={(value) => updateTemplate("workorder", "showTechnicianSignature", value)} disabled={readOnly} />
                <CheckboxField label="Bedrijfsgegevens tonen" checked={workorder.showCompanyDetails} onChange={(value) => updateTemplate("workorder", "showCompanyDetails", value)} disabled={readOnly} />
                <CheckboxField label="QR-code tonen" checked={workorder.showQrCode} onChange={(value) => updateTemplate("workorder", "showQrCode", value)} disabled={readOnly} />
              </div>
              <div className="mt-3">
                <LayoutPresetField
                  value={workorder.layoutPreset}
                  onChange={(value) => updateTemplate("workorder", "layoutPreset", value)}
                  disabled={readOnly}
                />
              </div>
            </SectionCard>

            <SectionCard title="Mandagstaat">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <CheckboxField label="Per monteur tonen" checked={mandayStatement.showPerTechnician} onChange={(value) => updateTemplate("mandayStatement", "showPerTechnician", value)} disabled={readOnly} />
                <CheckboxField label="Totalen tonen" checked={mandayStatement.showTotals} onChange={(value) => updateTemplate("mandayStatement", "showTotals", value)} disabled={readOnly} />
                <CheckboxField label="Accorderingsblok tonen" checked={mandayStatement.showApprovalBlock} onChange={(value) => updateTemplate("mandayStatement", "showApprovalBlock", value)} disabled={readOnly} />
              </div>
              <div className="mt-3">
                <LayoutPresetField
                  value={mandayStatement.layoutPreset}
                  onChange={(value) => updateTemplate("mandayStatement", "layoutPreset", value)}
                  disabled={readOnly}
                />
              </div>
            </SectionCard>

            <SectionCard title="Inspectierapport">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <CheckboxField label="Bevindingen tonen" checked={inspectionReport.showFindings} onChange={(value) => updateTemplate("inspectionReport", "showFindings", value)} disabled={readOnly} />
                <CheckboxField label="Foto's tonen" checked={inspectionReport.showPhotos} onChange={(value) => updateTemplate("inspectionReport", "showPhotos", value)} disabled={readOnly} />
                <CheckboxField label="Risicoclassificatie tonen" checked={inspectionReport.showRiskClassification} onChange={(value) => updateTemplate("inspectionReport", "showRiskClassification", value)} disabled={readOnly} />
                <CheckboxField label="Advies tonen" checked={inspectionReport.showAdvice} onChange={(value) => updateTemplate("inspectionReport", "showAdvice", value)} disabled={readOnly} />
              </div>
              <div className="mt-3">
                <LayoutPresetField
                  value={inspectionReport.layoutPreset}
                  onChange={(value) => updateTemplate("inspectionReport", "layoutPreset", value)}
                  disabled={readOnly}
                />
              </div>
            </SectionCard>

            {canViewDocumentLibrary ? (
              <SectionCard
                title="Documentbibliotheek"
                description="Beheer centrale documenten, templates en bijlagen die later gebruikt worden bij werkbonnen, mandagstaten, inspecties en rapportages."
              >
                <p className="mb-3 text-sm text-slate-600">
                  Huisstijl & Documenten is de instellingenlaag. De Documentbibliotheek is de opslag- en koppelingenlaag.
                </p>
                <Link
                  to="/tenant-admin/bedrijfsinrichting/documenten"
                  className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Open Documentbibliotheek
                </Link>
              </SectionCard>
            ) : null}

            {!readOnly ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={saveSettings}
                  disabled={saving || loading}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {saving ? "Opslaan..." : "Instellingen opslaan"}
                </button>
              </div>
            ) : null}
          </div>

          <div className="lg:sticky lg:top-4 lg:self-start">
            <PreviewCard settings={settings} />
          </div>
        </div>
      </div>
    </div>
  );
}
